import { useMemo } from 'react'
import { CoverLetter, Optional, PageFormatType, Resume } from 'packages/types'
import { camelCase } from 'builder/utils/camelCase'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { selectors } from 'builder/modules/generalEditor'
import { SelectOption, SelectValue } from 'builder/components/Select/types'
import { useI18n } from 'builder/hooks/useI18n'
import { A4_FACTOR, US_LETTER_FACTOR } from 'builder/modules/constants'
import { LayoutFormats } from '../constants'

export const useTemplateCustomization = (currentDocument: Optional<Resume | CoverLetter>) => {
  const templates = useTypedSelector(selectors.resumeTemplates)
  const { i18n } = useI18n()
  const currentTemplate = templates.find(template => template.id === currentDocument?.template)
  const templatesSettings =
    currentDocument && 'templateSettings' in currentDocument
      ? currentDocument?.templateSettings
      : undefined
  const resumeSpecificTemplateSettings =
    currentDocument?.template && templatesSettings?.[camelCase(currentDocument?.template)]

  const { supportsLayoutFormats, supportsNewCustomization, supportsCustomColor, settings } =
    currentTemplate || {}

  const LAYOUT_FORMAT_OPTIONS = useMemo(() => {
    return [
      {
        id: LayoutFormats.A4,
        name: i18n.t(
          'builder.resume_editor.document_customization.sidebar.layout_tab.layout_format_a4',
        ),
        factor: A4_FACTOR,
      },
      {
        id: LayoutFormats.US_LETTER,
        name: i18n.t(
          'builder.resume_editor.document_customization.sidebar.layout_tab.layout_format_us_letter',
        ),
        factor: US_LETTER_FACTOR,
      },
    ]
  }, [i18n])

  const convertToSelectOption = <V extends SelectValue>(
    data: Record<string, { id: V; name: string }> | { id: V; name: string }[],
  ): SelectOption<V>[] => {
    const arrayData = Array.isArray(data) ? data : Object.values(data)
    return arrayData.map(item => ({
      id: item.id,
      name: item.name,
    }))
  }

  const letterFormatOptions = useMemo(() => {
    return convertToSelectOption(LAYOUT_FORMAT_OPTIONS)
  }, [LAYOUT_FORMAT_OPTIONS])

  const selectedLayoutFormatOption = useMemo((): PageFormatType => {
    if (!resumeSpecificTemplateSettings?.format) {
      return settings?.format || LayoutFormats.A4
    }
    return resumeSpecificTemplateSettings.format
  }, [resumeSpecificTemplateSettings?.format, settings?.format])

  return {
    resume: currentDocument,
    templates,
    currentTemplate,
    letterFormatOptions,
    supportsLayoutFormats,
    selectedLayoutFormatOption,
    templateName: currentDocument?.template,
    templatesSettings,
    resumeSpecificTemplateSettings,
    supportsCustomColor,
    supportsNewCustomization,
  }
}
